import { useCallback, useEffect, useRef, useState } from 'react';

import { dados } from '@/data/porta';
import type { PareamentoDoCanal } from '@/data/tipos';

/** De quanto em quanto tempo se pergunta ao servidor se alguém já leu o código. */
export const INTERVALO_DO_PAREAMENTO_MS = 3000;

/**
 * O QR da UAZAPI vale por cerca de um minuto. Passado isso ele continua na tela,
 * bonito e inútil: quem aponta o telefone recebe "código expirado" do próprio
 * WhatsApp e conclui que o produto está quebrado. Por isso a tela conta o tempo
 * e troca a imagem por um botão antes de o código morrer.
 */
export const VALIDADE_DO_QR_S = 55;

/** Consultas seguidas sem resposta até a tela dizer que o servidor sumiu. */
export const FALHAS_ATE_AVISAR = 3;

export interface Pareamento {
    estado: PareamentoDoCanal | null;
    erro: string | null;
    gerando: boolean;
    restante: number;
    falhas: { seguidas: number; motivo: string };
    conectado: boolean;
    temQr: boolean;
    gerar: (acao: 'criar' | 'conectar') => Promise<void>;
}

/**
 * O pareamento do WhatsApp: gere, leia, conecte. Usado pelo modal de
 * Configurações e pelo cartão de canal da VecchIA.
 *
 * A conexão é **assíncrona e do outro lado**: quem decide se pareou é o celular
 * de alguém, e nada na tela sabe disso até perguntar. Daí o formato — um pedido
 * a cada três segundos, uma contagem que diz quando o código vence e um botão
 * para pedir outro.
 */
export function usePareamento(canalId: string | null, inicial: PareamentoDoCanal | null | undefined, onPareado: () => void): Pareamento {
    const [estado, setEstado] = useState<PareamentoDoCanal | null>(inicial ?? null);
    const [erro, setErro] = useState<string | null>(null);
    const [gerando, setGerando] = useState(false);
    const [restante, setRestante] = useState(inicial?.qrcode ? VALIDADE_DO_QR_S : 0);
    const [falhas, setFalhas] = useState({ seguidas: 0, motivo: '' });

    /* Guarda o aviso de quem já pareou, para não avisar duas vezes se a
       consulta seguinte chegar antes de a lista recarregar. */
    const avisado = useRef(false);
    const aoParear = useRef(onPareado);
    useEffect(() => { aoParear.current = onPareado; });

    const conectado = estado?.status === 'conectado';
    const temQr = Boolean(estado?.qrcode) && restante > 0;

    useEffect(() => {
        setEstado(inicial ?? null);
        setErro(null);
        setRestante(inicial?.qrcode ? VALIDADE_DO_QR_S : 0);
        setFalhas({ seguidas: 0, motivo: '' });
        avisado.current = false;
    }, [inicial, canalId]);

    const gerar = useCallback(async (acao: 'criar' | 'conectar') => {
        if (!canalId) return;
        setGerando(true);
        setErro(null);
        try {
            const r = await dados().parearCanal(canalId, acao);
            setEstado(r);
            setRestante(r.qrcode ? VALIDADE_DO_QR_S : 0);
            avisado.current = false;
            if (r.status === 'conectado' && !avisado.current) {
                avisado.current = true;
                aoParear.current();
            }
        } catch (e) {
            setErro(e instanceof Error ? e.message : 'Não foi possível gerar o código.');
        } finally {
            setGerando(false);
        }
    }, [canalId]);

    /* A contagem regressiva do código à vista. */
    useEffect(() => {
        if (!canalId || conectado || restante <= 0) return;
        const relogio = setInterval(() => setRestante((s) => (s > 0 ? s - 1 : 0)), 1000);
        return () => clearInterval(relogio);
    }, [canalId, conectado, restante]);

    /* A pergunta que só o servidor responde: já leram? */
    useEffect(() => {
        if (!canalId || conectado) return;

        let vivo = true;
        const perguntar = async () => {
            try {
                const r = await dados().parearCanal(canalId, 'status');
                if (!vivo) return;
                setFalhas({ seguidas: 0, motivo: '' });
                setEstado((anterior) => ({
                    ...r,
                    /* O servidor às vezes devolve o status sem o QR. Perder a
                       imagem por causa disso faria o código sumir da tela no
                       meio de uma leitura. */
                    ...(r.qrcode ? {} : anterior?.qrcode ? { qrcode: anterior.qrcode } : {}),
                }));
                if (r.status === 'conectado' && !avisado.current) {
                    avisado.current = true;
                    aoParear.current();
                }
            } catch (e) {
                /* Uma consulta que falha não derruba o pareamento; várias
                   seguidas, sim, aparecem — senão o servidor fora do ar vira uma
                   espera sem fim diante de um código já lido. */
                if (!vivo) return;
                const motivo = e instanceof Error ? e.message : 'sem resposta';
                setFalhas((f) => ({ seguidas: f.seguidas + 1, motivo }));
            }
        };

        const relogio = setInterval(perguntar, INTERVALO_DO_PAREAMENTO_MS);
        return () => { vivo = false; clearInterval(relogio); };
    }, [canalId, conectado]);

    return { estado, erro, gerando, restante, falhas, conectado, temQr, gerar };
}
